// View: pure functions from notes to detached DOM nodes.
// The retry handler is injected (onRetry) so this module has no dependency on
// the send/render controller — keeping the dependency graph acyclic.

import type { Note } from "./types.js";
import { DEST_LABEL } from "./types.js";
import { DIV, SPAN, BUTTON } from "./dom.js";
import { timeLabel } from "./format.js";
import { groupByDay } from "./note.js";

export type RetryFn = (id: string) => void;

const bubbleClass = (note: Note): string =>
  `bubble bubble--${note.status}` +
  (note.status === "error" ? " bubble--clickable" : "");

const renderMeta = (note: Note): Node => {
  const destLabel = DEST_LABEL[note.dest];
  return DIV("bubble__meta", [
    ...(destLabel ? [SPAN("bubble__dest", destLabel)] : []),
    SPAN("bubble__time", timeLabel(note.timestamp)),
  ]);
};

const renderAction = (note: Note, onRetry: RetryFn): readonly Node[] =>
  note.status === "pending"
    ? [
        BUTTON(
          { class: "bubble__retry", on: { click: () => onRetry(note.id) } },
          "réessayer",
        ),
      ]
    : [];

const renderBubble = (note: Note, onRetry: RetryFn): Node => {
  const bubble = DIV(
    {
      class: bubbleClass(note),
      dataset: { id: note.id },
      // Clicking an error bubble retries the send.
      on:
        note.status === "error"
          ? { click: () => onRetry(note.id) }
          : undefined,
    },
    [DIV("bubble__text", note.text), renderMeta(note), ...renderAction(note, onRetry)],
  );
  return DIV("row", [bubble]);
};

const renderDaySeparator = (day: string): Node => DIV("day-sep", day);

export const viewNodes = (
  notes: readonly Note[],
  onRetry: RetryFn,
): readonly Node[] =>
  groupByDay(notes).flatMap((group) => [
    renderDaySeparator(group.day),
    ...group.notes.map((note) => renderBubble(note, onRetry)),
  ]);
